import { type BufferMathOutput, bufferMathFromFund, formatPct } from "@/data/buffer-math";
import { type LiveNavResult, fetchDailyNav } from "@/data/etf-api";
import type { FundDetail } from "@/data/fund-detail-types";
import { useEtfLivePrices } from "@/hooks/use-etf-live-prices";
import { useEffect, useRef, useState } from "react";

export function BufferOutcomePeriod({ fund }: { fund: FundDetail }) {
  const refTicker = fund.referenceTicker ?? "REF";
  const fundTicker = fund.ticker;

  const { prices } = useEtfLivePrices();
  const refPriceNow = prices[refTicker.toUpperCase()]?.price;

  const [navData, setNavData] = useState<LiveNavResult | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchDailyNav(fundTicker).then((result) => {
      if (!cancelled) setNavData(result);
    });
    return () => {
      cancelled = true;
    };
  }, [fundTicker]);

  const fundNavNow = toNum(navData?.latest?.nav);
  const periodStartStr = fund.outcomePeriodStart
    ? new Date(`${fund.outcomePeriodStart} 12:00:00`).toISOString().split("T")[0]
    : null;
  const firstInPeriodRecord = periodStartStr
    ? navData?.records?.find((r) => (r.rate_date.split("T")[0] ?? "") >= periodStartStr)
    : navData?.records?.[0];
  const fundNavAtPeriodStart = toNum(firstInPeriodRecord?.nav);

  const math: BufferMathOutput | null = bufferMathFromFund(fund, {
    refPriceNow,
    fundNavNow,
    fundNavAtPeriodStart,
  });

  const dateLabel = buildDateLabel(fund, math);
  const progressPct = math ? math.progress * 100 : 0;

  const fundReturnPct = math?.fundReturnPct ?? null;
  const bufferLevel = fund.bufferLevel ?? 0;

  const remainingBufferDisplay =
    fundReturnPct != null ? formatPct(fundReturnPct + bufferLevel) : "TBD";

  const downsideBefore =
    fundReturnPct == null ? "TBD" : fundReturnPct <= 0 ? "0.00%" : formatPct(-fundReturnPct);

  const capGrossDetail = fund.bufferOutcomeDetails?.find(
    (d) => d.label.toLowerCase().includes("cap") && d.label.toLowerCase().includes("gross"),
  );
  const capGrossDisplay =
    fund.startingCapGrossPct != null && Number.isFinite(fund.startingCapGrossPct)
      ? `${fund.startingCapGrossPct.toFixed(2)}%`
      : capGrossDetail?.value && capGrossDetail.value !== "TBD"
        ? capGrossDetail.value
        : "TBD";
  const remainingCapDisplay =
    math?.remainingCapPct != null ? formatPct(math.remainingCapPct) : "TBD";
  const remainingCapTooltip =
    math?.remainingCapPct != null
      ? `The cumulative maximum potential return available at the ETF's current price if held to the end of the current Outcome Period. The minimum index return required to realize the fund cap is ${formatPct(math.remainingCapPct)}`
      : undefined;

  const metrics = [
    { label: "Fund Return", value: "TBD", sub: "", tooltip: undefined as string | undefined },
    {
      label: `${refTicker} Return`,
      value: math?.refReturnPct != null ? formatPct(math.refReturnPct, { signed: true }) : "TBD",
      sub: "",
      tooltip: undefined as string | undefined,
    },
    {
      label: "Remaining Cap",
      value: remainingCapDisplay,
      sub: "Net of fees",
      tooltip: remainingCapTooltip,
    },
    {
      label: "Remaining Buffer",
      value: remainingBufferDisplay,
      sub: "Net of fees",
      tooltip: undefined as string | undefined,
    },
    {
      label: "Downside Before Buffer",
      value: downsideBefore,
      sub: "",
      tooltip: undefined as string | undefined,
    },
  ];

  return (
    <>
      <div
        id="sec-outcome"
        data-fund-section="bufferConfig"
        className="border-b border-[var(--etf-border)] px-10 max-md:px-4 max-[480px]:px-3 py-[18px] max-md:py-3.5 max-[480px]:py-3"
      >
        <div className="flex justify-between items-baseline mb-3 gap-3 max-md:flex-col max-md:gap-1">
          <div className="font-[family-name:var(--font-f37bolton)] text-lg font-semibold text-[var(--etf-white)]">
            Current Outcome Period Values
          </div>
          <div className="font-[family-name:var(--font-geist)] text-[11px] text-[var(--etf-white3)]">
            {dateLabel}
          </div>
        </div>

        <div className="h-[3px] bg-[var(--etf-border2)] rounded-sm mb-3.5 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[var(--etf-orange)] to-[#ff7733] rounded-sm transition-[width] duration-[800ms] ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="flex gap-0 border border-[var(--etf-border)] rounded max-md:flex-wrap overflow-hidden">
          {metrics.map((m) => (
            <div
              key={m.label}
              className={`flex-1 max-md:flex-[1_1_50%] max-md:min-w-0 max-[480px]:flex-[1_1_100%] px-4 max-[480px]:px-3 py-3.5 max-[480px]:py-3 border-r border-[var(--etf-border2)] last:border-r-0 max-md:[&:nth-child(even)]:border-r-0 max-[480px]:border-r-0 max-md:border-b max-md:border-b-[var(--etf-border2)]${m.tooltip ? " group relative" : ""}`}
            >
              <div className="font-[family-name:var(--font-geist)] text-[10px] tracking-[0.08em] uppercase text-[var(--etf-white3)] mb-2">
                {m.label}
              </div>
              <div className="font-[family-name:var(--font-geist)] text-lg max-[480px]:text-[15px] font-medium text-[var(--etf-white)]">
                {m.value}
              </div>
              {m.sub && (
                <div className="font-[family-name:var(--font-geist)] text-[10px] text-[var(--etf-white3)] mt-0.5 opacity-70">
                  {m.sub}
                </div>
              )}
              {m.tooltip && (
                <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-150 absolute bottom-full left-0 mb-2 z-50 w-72 bg-[#1a1a1a] border border-[var(--etf-border)] rounded p-3 text-[11px] text-[var(--etf-white2)] leading-[1.6] font-[family-name:var(--font-instrument)] shadow-xl">
                  {m.tooltip}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="font-[family-name:var(--font-geist)] text-[11.5px] font-medium text-[var(--etf-white)] mt-2.5 leading-normal text-justify">
          Current and net figures represent outcome period expenses already incurred, and yet to be
          incurred. See Outcome Details in sidebar for starting values. The Cap is determined by
          market conditions at the time the Fund establishes its FLEX Options positions at the
          beginning of each Outcome Period. For the current Outcome Period, the Cap is{" "}
          {capGrossDisplay} before Fund fees and expenses.
        </div>
      </div>

      <OutcomePerformanceChart
        fund={fund}
        navData={navData}
        refPriceNow={refPriceNow}
        refPriceAtPeriodStart={fund.refPriceAtPeriodStart}
      />
    </>
  );
}

function OutcomePerformanceChart({
  fund,
  navData,
  refPriceNow,
  refPriceAtPeriodStart,
}: {
  fund: FundDetail;
  navData: LiveNavResult | null;
  refPriceNow?: number;
  refPriceAtPeriodStart?: number;
}) {
  const refTicker = fund.referenceTicker ?? "REF";
  const fundTicker = fund.ticker;

  const capPct = fund.startingCapGrossPct;
  const bufferLevel = fund.bufferLevel ?? 0;
  const bufferStart = fund.bufferStart ?? 0;
  const bufferLineValue = bufferStart > 0 ? -bufferStart : -bufferLevel;
  const bufferLineLabel =
    bufferStart > 0
      ? `BUFFER START ${bufferLineValue.toFixed(2).replace(/^-/, "−")}%`
      : `BUFFER ${bufferLineValue.toFixed(2).replace(/^-/, "−")}%`;

  const periodStart = fund.outcomePeriodStart ? parseDateLoose(fund.outcomePeriodStart) : null;
  const periodEnd = fund.outcomePeriodEnd ? parseDateLoose(fund.outcomePeriodEnd) : null;
  const periodSpanMs = periodStart && periodEnd ? periodEnd.getTime() - periodStart.getTime() : 0;
  type FundPoint = { date: Date; progress: number; returnPct: number };
  const fundPoints: FundPoint[] = (() => {
    if (!navData || !periodStart || periodSpanMs <= 0) return [];
    type ParsedRecord = { date: Date; nav: number };
    const inPeriod: ParsedRecord[] = [];
    for (const r of navData.records) {
      const dateStr = r.rate_date.split("T")[0];
      if (!dateStr) continue;
      const d = new Date(`${dateStr}T12:00:00`);
      if (Number.isNaN(d.getTime())) continue;
      if (d.getTime() < periodStart.getTime()) continue;
      const nav = toNum(r.nav);
      if (nav == null || !Number.isFinite(nav) || nav <= 0) continue;
      inPeriod.push({ date: d, nav });
    }
    if (inPeriod.length === 0) return [];
    inPeriod.sort((a, b) => a.date.getTime() - b.date.getTime());
    const baselineNav = inPeriod[0]?.nav;
    if (!baselineNav || baselineNav === 0) return [];
    const out: FundPoint[] = [];
    const n = inPeriod.length;
    for (let i = 0; i < n; i++) {
      const r = inPeriod[i]!;
      const progress = n > 1 ? i / (n - 1) : 0;
      const returnPct = ((r.nav - baselineNav) / baselineNav) * 100;
      out.push({ date: r.date, progress, returnPct });
    }
    return out;
  })();

  const bufFloor =
    bufferLevel < 100 ? (bufferStart > 0 ? -(bufferStart + bufferLevel) : -bufferLevel) : 0;
  const capCeil = capPct ?? 0;
  const observedMin = fundPoints.length > 0 ? Math.min(...fundPoints.map((p) => p.returnPct)) : 0;
  const observedMax = fundPoints.length > 0 ? Math.max(...fundPoints.map((p) => p.returnPct)) : 0;
  let yMin = Math.min(0, bufFloor, observedMin);
  let yMax = Math.max(0, capCeil, observedMax);
  yMin = Math.floor(yMin / 5) * 5 - 5;
  yMax = Math.ceil(yMax / 5) * 5 + 5;
  if (yMin > -10) yMin = -10;
  if (yMax < 10) yMax = 10;
  const Y_MIN = yMin;
  const Y_MAX = yMax;
  const Y_STEP = 5;
  const yTicks: number[] = [];
  for (let v = Y_MIN; v <= Y_MAX; v += Y_STEP) yTicks.push(v);

  const PAD = { top: 24, right: 20, bottom: 40, left: 56 };
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number }>({
    w: 800,
    h: 320,
  });
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      setDims((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const VB_W = dims.w;
  const VB_H = dims.h;
  const plotW = Math.max(0, VB_W - PAD.left - PAD.right);
  const plotH = Math.max(0, VB_H - PAD.top - PAD.bottom);
  const yToPx = (v: number) => PAD.top + ((Y_MAX - v) / (Y_MAX - Y_MIN)) * plotH;

  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  type Tooltip = {
    asOfDate: string;
    etfNav: string;
    remainingCap: string;
    remainingBuffer: string;
    downsideBeforeBuffer: string;
    remainingDaysLabel: string;
    refValueReturn: string;
  };
  const lastIdx = fundPoints.length - 1;
  const tooltips: Tooltip[] = fundPoints.map((p, i) => {
    const records = navData?.records ?? [];
    const recordForDate = records.find((r) => {
      const rd = r.rate_date.split("T")[0];
      return rd === p.date.toISOString().split("T")[0];
    });
    const nav = recordForDate ? toNum(recordForDate.nav) : undefined;
    const etfNav = nav != null ? `$${nav.toFixed(2)}` : "—";
    const capNet = fund.startingCapNetPct ?? 0;
    const remainingCapPct =
      capNet > 0 && Number.isFinite(p.returnPct)
        ? ((1 + capNet / 100) / (1 + p.returnPct / 100) - 1) * 100
        : null;
    const remainingCap = remainingCapPct != null ? `${remainingCapPct.toFixed(2)}%` : "—";
    const remainingMs = periodEnd ? periodEnd.getTime() - p.date.getTime() : 0;
    const remainingDays = Math.max(0, Math.ceil(remainingMs / 86_400_000));
    const remainingDaysLabel = `${remainingDays} days`;
    const isLatest = i === lastIdx;
    const fundReturnAtPoint = p.returnPct;
    const remainingBuffer = `${(fundReturnAtPoint + bufferLevel).toFixed(2)}%`;
    const downsideBeforeBuffer = fundReturnAtPoint <= 0 ? "0.00%" : formatPct(-fundReturnAtPoint);
    let refValueReturn = "—";
    if (
      isLatest &&
      refPriceNow != null &&
      refPriceAtPeriodStart != null &&
      refPriceAtPeriodStart > 0
    ) {
      const refReturnPct = (refPriceNow / refPriceAtPeriodStart - 1) * 100;
      refValueReturn = `$${refPriceNow.toFixed(2)} / ${
        refReturnPct >= 0 ? "+" : ""
      }${refReturnPct.toFixed(2)}%`;
    }
    return {
      asOfDate: `${p.date.getMonth() + 1}/${p.date.getDate()}/${p.date.getFullYear()}`,
      etfNav,
      remainingCap,
      remainingBuffer,
      downsideBeforeBuffer,
      remainingDaysLabel,
      refValueReturn,
    };
  });

  return (
    <div
      id="sec-op-perf"
      className="border-b border-[var(--etf-border)] px-10 max-md:px-4 max-[480px]:px-3 py-6"
    >
      <div className="flex items-start justify-between gap-4 mb-5 max-md:flex-col border-b border-[var(--etf-border)] -mx-10 px-10 pb-6 max-md:-mx-4 max-md:px-4 max-[480px]:-mx-3 max-[480px]:px-3">
        <div>
          <h2 className="font-[family-name:var(--font-f37bolton)] text-[24px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3">
            <span className="inline-block w-[3px] h-5 bg-[var(--etf-orange)]" />
            Outcome Period Performance
          </h2>
          <p className="font-[family-name:var(--font-geist)] text-[12px] text-[var(--etf-white3)] tracking-[0.08em] mt-1 pl-[15px]">
            Cumulative Return Since Outcome Period Start
          </p>
        </div>
        <div className="flex gap-4 self-end max-md:self-start flex-wrap">
          <div className="flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)]">
            <span className="w-4 h-[2.5px] rounded-sm shrink-0 bg-[var(--etf-orange)]" />
            <span>{fundTicker} Return</span>
          </div>
          <div className="flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)]">
            <span className="w-4 h-0 shrink-0 border-t-2 border-dashed border-[var(--etf-white3)] opacity-40" />
            <span>{refTicker} Return</span>
          </div>
        </div>
      </div>

      <div
        ref={wrapRef}
        className="relative h-[340px] max-md:h-[240px] max-[480px]:h-[200px] mt-5 border border-[var(--etf-border)] rounded overflow-visible bg-[var(--etf-black)]"
      >
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          width={VB_W}
          height={VB_H}
          className="block w-full h-full"
          role="img"
          aria-label={`Outcome period performance chart showing ${fundTicker} return versus ${refTicker} return over time`}
          onMouseMove={(e) => {
            if (fundPoints.length === 0) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const xVb = ((e.clientX - rect.left) / rect.width) * VB_W;
            let bestIdx = 0;
            let bestDist = Number.POSITIVE_INFINITY;
            for (let i = 0; i < fundPoints.length; i++) {
              const px = PAD.left + fundPoints[i]!.progress * plotW;
              const d = Math.abs(px - xVb);
              if (d < bestDist) {
                bestDist = d;
                bestIdx = i;
              }
            }
            setHoverIdx(bestIdx);
          }}
          onMouseLeave={() => setHoverIdx(null)}
        >
          {yTicks.map((v) => {
            const py = Math.round(yToPx(v)) + 0.5;
            const isZero = v === 0;
            return (
              <g key={v}>
                <line
                  x1={PAD.left}
                  x2={PAD.left + plotW}
                  y1={py}
                  y2={py}
                  stroke={isZero ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.06)"}
                  strokeWidth={1}
                />
                <text
                  x={PAD.left - 8}
                  y={py + 4}
                  textAnchor="end"
                  fontFamily="'F37 Bolton', sans-serif"
                  fontSize={10}
                  fontWeight={500}
                  fill="rgba(255,255,255,0.45)"
                >
                  {v > 0 ? `+${v}%` : `${v}%`}
                </text>
              </g>
            );
          })}

          {capPct != null && capPct >= Y_MIN && capPct <= Y_MAX && (
            <g>
              <line
                x1={PAD.left}
                x2={PAD.left + plotW}
                y1={Math.round(yToPx(capPct)) + 0.5}
                y2={Math.round(yToPx(capPct)) + 0.5}
                stroke="rgba(58,219,106,0.55)"
                strokeWidth={1}
                strokeDasharray="5 4"
              />
              <text
                x={PAD.left + 6}
                y={Math.round(yToPx(capPct)) - 5}
                fontFamily="'F37 Bolton', sans-serif"
                fontSize={9}
                fontWeight={500}
                fill="rgba(58,219,106,0.8)"
              >
                CAP +{capPct.toFixed(2)}%
              </text>
            </g>
          )}

          {bufferLevel > 0 &&
            bufferLevel < 100 &&
            bufferLineValue >= Y_MIN &&
            bufferLineValue <= Y_MAX && (
              <g>
                <line
                  x1={PAD.left}
                  x2={PAD.left + plotW}
                  y1={Math.round(yToPx(bufferLineValue)) + 0.5}
                  y2={Math.round(yToPx(bufferLineValue)) + 0.5}
                  stroke="rgba(255,85,0,0.4)"
                  strokeWidth={1}
                  strokeDasharray="5 4"
                />
                <text
                  x={PAD.left + 6}
                  y={Math.round(yToPx(bufferLineValue)) + 13}
                  fontFamily="'F37 Bolton', sans-serif"
                  fontSize={9}
                  fontWeight={500}
                  fill="rgba(255,85,0,0.8)"
                >
                  {bufferLineLabel}
                </text>
              </g>
            )}

          {fundPoints.length >= 2 && (
            <polyline
              fill="none"
              stroke="var(--etf-orange)"
              strokeWidth={2}
              strokeDasharray="2 3"
              strokeLinejoin="round"
              strokeLinecap="round"
              points={fundPoints
                .map((p) => `${PAD.left + p.progress * plotW},${yToPx(p.returnPct)}`)
                .join(" ")}
            />
          )}

          {hoverIdx !== null &&
            fundPoints[hoverIdx] &&
            (() => {
              const hx = Math.round(PAD.left + fundPoints[hoverIdx]?.progress * plotW) + 0.5;
              return (
                <line
                  x1={hx}
                  x2={hx}
                  y1={PAD.top}
                  y2={PAD.top + plotH}
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
              );
            })()}

          {fundPoints.map((p, i) => {
            const cx = PAD.left + p.progress * plotW;
            const cy = yToPx(p.returnPct);
            const isLast = i === fundPoints.length - 1;
            const isHover = hoverIdx === i;
            return (
              <g key={`pt-${i}`}>
                {isHover && <circle cx={cx} cy={cy} r={8} fill="rgba(255,85,0,0.18)" />}
                <circle
                  cx={cx}
                  cy={cy}
                  r={isLast || isHover ? 3.5 : 2.5}
                  fill="var(--etf-orange)"
                  stroke={isLast ? "rgba(255,85,0,0.25)" : "transparent"}
                  strokeWidth={isLast ? 4 : 0}
                />
                <circle
                  cx={cx}
                  cy={cy}
                  r={12}
                  fill="transparent"
                  pointerEvents="all"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(null)}
                />
              </g>
            );
          })}

          {fundPoints.length > 0
            ? (() => {
                const labelCount = VB_W < 500 ? 4 : 6;
                const n = fundPoints.length;
                const seen = new Set<number>();
                const out: React.ReactNode[] = [];
                for (let li = 0; li < labelCount; li++) {
                  const di = Math.round((li / (labelCount - 1)) * (n - 1));
                  if (seen.has(di)) continue;
                  seen.add(di);
                  const p = fundPoints[di]!;
                  const lx = PAD.left + p.progress * plotW;
                  out.push(
                    <text
                      key={`xtick-${di}`}
                      x={Math.round(lx)}
                      y={PAD.top + plotH + 18}
                      textAnchor="middle"
                      fontFamily="'Instrument Sans', sans-serif"
                      fontSize={10}
                      fontWeight={500}
                      fill="rgba(255,255,255,0.45)"
                    >
                      {formatTickDate(p.date)}
                    </text>,
                  );
                }
                return out;
              })()
            : MONTH_TICK_NAMES.map((mName, m) => {
                const mx = PAD.left + (m / 11) * plotW;
                return (
                  <text
                    key={`xtick-month-${mName}`}
                    x={Math.round(mx)}
                    y={PAD.top + plotH + 18}
                    textAnchor="middle"
                    fontFamily="'Instrument Sans', sans-serif"
                    fontSize={10}
                    fontWeight={500}
                    fill="rgba(255,255,255,0.45)"
                  >
                    {mName}
                  </text>
                );
              })}
        </svg>

        {hoverIdx !== null &&
          fundPoints[hoverIdx] &&
          tooltips[hoverIdx] &&
          (() => {
            const p = fundPoints[hoverIdx]!;
            const t = tooltips[hoverIdx]!;
            const TIP_W = Math.min(232, Math.max(160, VB_W - 16));
            const TIP_H = 200;
            const pointX = PAD.left + p.progress * plotW;
            const pointY = yToPx(p.returnPct);
            const rightCandidate = pointX + 14;
            const wouldOverflowRight = rightCandidate + TIP_W > VB_W - 8;
            const leftPx = wouldOverflowRight
              ? Math.max(8, pointX - 14 - TIP_W)
              : Math.max(8, rightCandidate);
            const desiredTop = pointY - 18;
            const fitsInside = TIP_H <= VB_H - 16;
            const topPx = fitsInside ? Math.max(8, Math.min(VB_H - TIP_H - 8, desiredTop)) : 8;
            return (
              <div
                className="absolute pointer-events-none z-50 rounded-sm border border-[var(--etf-border)] bg-[var(--etf-black2)] shadow-[0_6px_24px_rgba(0,0,0,0.45)] px-3 py-2"
                style={{ left: leftPx, top: topPx, width: TIP_W }}
              >
                <div className="font-[family-name:var(--font-instrument)] italic text-[10px] text-[var(--etf-white3)] tracking-[0.04em]">
                  As of {t.asOfDate}
                </div>
                <div className="font-[family-name:var(--font-f37bolton)] text-[12px] font-semibold text-[var(--etf-white)] mt-0.5 mb-1.5">
                  {fundTicker} (Net)
                </div>
                <TooltipRow label="ETF NAV" value={t.etfNav} />
                <TooltipRow label="Remaining Cap" value={t.remainingCap} />
                <TooltipRow label="Remaining Buffer" value={t.remainingBuffer} />
                <TooltipRow label="Downside Before Buffer" value={t.downsideBeforeBuffer} />
                <div className="my-1.5 border-t border-[var(--etf-border2)]" />
                <TooltipRow label="Remaining Outcome Period" value={t.remainingDaysLabel} />
                <TooltipRow label={`${refTicker} Value/Return`} value={t.refValueReturn} />
              </div>
            );
          })()}
      </div>

      <div className="flex items-start justify-between gap-6 mt-4 max-md:flex-col">
        <p className="font-[family-name:var(--font-geist)] text-[11.5px] font-medium text-[var(--etf-white)] leading-[1.5] m-0 max-w-[640px]">
          Past performance does not guarantee future results. Returns shown are cumulative since the
          start of the current outcome period and are based on market price. The cap and buffer
          shown are starting values, before fees and expenses.
        </p>
        <button
          type="button"
          disabled
          aria-label="Download outcome period chart data as CSV"
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 border border-[var(--etf-border)] rounded-[3px] text-[var(--etf-white3)] font-[family-name:var(--font-geist)] text-[11px] bg-transparent whitespace-nowrap shrink-0 transition-[border-color,color] duration-150 hover:border-[var(--etf-orange)] hover:text-[var(--etf-orange)] opacity-50 cursor-not-allowed disabled:cursor-not-allowed"
        >
          <span className="text-[13px] leading-none" aria-hidden="true">
            &#8595;
          </span>
          <span>Download Chart Data (CSV)</span>
        </button>
      </div>
    </div>
  );
}

function TooltipRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 font-[family-name:var(--font-instrument)] text-[11px] leading-tight py-[2px]">
      <span className="text-[var(--etf-white3)]">{label}</span>
      <span className="text-[var(--etf-white)] font-medium tabular-nums">{value}</span>
    </div>
  );
}

const MONTH_TICK_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function formatTickDate(d: Date): string {
  return `${MONTH_TICK_NAMES[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}`;
}

function parseDateLoose(dateStr: string): Date | null {
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const d = new Date(`${dateStr.split("T")[0]}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toNum(v: number | string | null | undefined): number | undefined {
  if (v == null) return undefined;
  if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : undefined;
}

function buildDateLabel(fund: FundDetail, math: BufferMathOutput | null): string {
  if (!math || !fund.outcomePeriodStart || !fund.outcomePeriodEnd) return "";
  if (math.state === "pre-launch") {
    return `Outcome period begins ${fund.outcomePeriodStart} · ${math.remainingDays} days until start`;
  }
  if (math.state === "post-period") {
    return "Outcome period ended";
  }
  const asOf = fund.holdingsAsOf ?? fund.dateLabel;
  if (!asOf) return `${math.remainingDays} days remaining`;
  if (/^pre[-\s]?launch$/i.test(asOf)) {
    return `Outcome period begins ${fund.outcomePeriodStart} · ${math.remainingDays} days until start`;
  }
  return `As of ${asOf} · ${math.remainingDays} days remaining`;
}
